import {Component, EventEmitter, Input, OnInit, Output, ViewChild, inject, signal} from '@angular/core';
import {RecipeRatingService} from '../../services/recipe/recipe-rating.service';
import {MyRating, RatingSummary} from '../../services/recipe/recipe.service';
import {UiModalComponent, UiStarBarComponent, UiToastService} from '../../shared/ui';
import {RatingInputComponent} from './rating-input.component';
import {RatingCommentBoxComponent} from './rating-comment-box.component';
import {RatingListComponent} from './rating-list.component';
import {AppLogger, AppLoggerService} from '../../core/logging';

/**
 * The rating surface on the recipe detail page: aggregate average, the
 * interactive personal input (+ optional note), and the lazy comment view.
 *
 * Owns the `summary` and `myRating` signals and reconciles the aggregate
 * optimistically. Because PUT/DELETE return 204 (no fresh average), we recompute
 * the new average exactly from the prior state — snappy — and let the next
 * detail fetch be the source of truth (1-decimal rounding may drift the last
 * digit; it self-heals on reload). On failure we roll back to the snapshot.
 *
 * The full comment view lives in a modal opened from the aggregate bar. It stays
 * lazy: `app-rating-list` is only mounted while the modal is open, so it fetches
 * `GET …/ratings` on demand and refetches whenever `reloadToken` changes.
 */
@Component({
  selector: 'app-recipe-ratings',
  standalone: true,
  imports: [
    UiStarBarComponent,
    UiModalComponent,
    RatingInputComponent,
    RatingCommentBoxComponent,
    RatingListComponent,
  ],
  template: `
    <div class="text-sm">
      <!-- One quiet line: aggregate · your rating · comment toggle -->
      <div class="flex items-center flex-wrap gap-x-3 gap-y-2 text-base-content/70">
        @if (summary().count > 0) {
          <!-- The aggregate bar is the affordance: tap it to read every rating -->
          <button
            type="button"
            class="inline-flex items-center gap-1.5 hover:text-base-content transition-colors cursor-pointer"
            [attr.aria-label]="'Read all ' + summary().count + ' ratings'"
            (click)="openRatings()">
            <ui-star-bar [avg]="summary().average" [count]="summary().count" size="sm"/>
          </button>
        } @else {
          <span class="inline-flex items-center gap-1.5 text-base-content/50">
            <ui-star-bar [avg]="0" [showValue]="false" size="sm"/>
            No ratings yet
          </span>
        }

        @if (canRate) {
          <span class="text-base-content/20 select-none" aria-hidden="true">·</span>
          <span class="inline-flex items-center gap-2">
            <span class="text-base-content/50">You</span>
            <app-rating-input
              size="sm"
              [score]="myRating()?.score ?? 0"
              [disabled]="busy()"
              name="my-recipe-rating"
              (scoreChange)="setScore($event)"
              (clear)="clearRating()"/>

            @if ((myRating()?.score ?? 0) > 0) {
              <button type="button" class="link link-hover text-base-content/50"
                      (click)="showComment.set(!showComment())">
                {{ (myRating()?.comment) ? 'Edit note' : 'Add note' }}
              </button>
            }
          </span>
        }
      </div>

      <!-- Note editor: only when the user opts in -->
      @if (canRate && (myRating()?.score ?? 0) > 0 && showComment()) {
        <div class="max-w-xl">
          <app-rating-comment-box
            [initial]="myRating()?.comment ?? ''"
            [disabled]="busy()"
            (save)="onNoteSaved($event)"/>
        </div>
      }
    </div>

    <!-- Comment view (lazy): mounted only while the modal is open -->
    <ui-modal #ratingsModal title="Ratings & Reviews" size="md" (modalClosed)="onModalClosed()">
      @if (modalOpen()) {
        <!-- keyed on the reload token so mutating my rating refetches the list -->
        @for (token of [reloadToken()]; track token) {
          <app-rating-list [recipeId]="recipeId"/>
        }
      }
    </ui-modal>
  `,
})
export class RecipeRatingsComponent implements OnInit {
  @Input({required: true}) recipeId!: number;

  /** Aggregate from the detail payload. */
  @Input() initialRating?: RatingSummary;

  /** The current user's rating from the detail payload, if any. */
  @Input() initialMyRating?: MyRating;

  /** Hide the interactive input when the caller doesn't want this user rating. */
  @Input() canRate = true;

  /**
   * Emitted when the server reports the recipe is no longer accessible (403),
   * so the parent can refetch/leave. We do not retry.
   */
  @Output() accessRevoked = new EventEmitter<void>();

  @ViewChild('ratingsModal') private ratingsModal?: UiModalComponent;

  readonly summary = signal<RatingSummary>({average: 0, count: 0});
  readonly myRating = signal<MyRating | undefined>(undefined);
  readonly busy = signal<boolean>(false);
  /** Whether the ratings modal is open — gates the lazy mount of the list. */
  readonly modalOpen = signal<boolean>(false);
  /** Whether the note editor is revealed (kept collapsed to stay unobtrusive). */
  readonly showComment = signal<boolean>(false);
  /** Bumped after a successful mutation to force the comment view to refetch. */
  readonly reloadToken = signal<number>(0);

  private ratingService = inject(RecipeRatingService);
  private toast = inject(UiToastService);
  private readonly log: AppLogger;

  constructor(appLogger: AppLoggerService) {
    this.log = appLogger.createForClass(RecipeRatingsComponent);
  }

  ngOnInit(): void {
    this.summary.set(this.initialRating ?? {average: 0, count: 0});
    this.myRating.set(this.initialMyRating);
  }

  /** Open the modal with everyone's ratings and notes. No-op when unrated. */
  openRatings(): void {
    if (this.summary().count === 0) return;
    this.modalOpen.set(true);
    this.ratingsModal?.open();
  }

  onModalClosed(): void {
    this.modalOpen.set(false);
  }

  private onMutated(): void {
    this.busy.set(false);
    // Refetch the comment view if it's open so it reflects my change.
    this.reloadToken.update(t => t + 1);
  }

  /** Set or replace my score; keeps any existing comment. */
  setScore(score: number): void {
    if (score < 1 || score > 5) return;

    const prev = this.snapshot();
    const prevScore = prev.myRating?.score ?? 0;

    this.myRating.set({score, comment: prev.myRating?.comment});
    this.summary.set(this.recompute(prev.summary, prevScore, score));

    this.busy.set(true);
    this.ratingService.rate(this.recipeId, score, prev.myRating?.comment ?? '').subscribe({
      next: () => this.onMutated(),
      error: (err) => this.handleError(err, prev, 'Could not save your rating.'),
    });
  }

  /** Clear my rating entirely. */
  clearRating(): void {
    const prev = this.snapshot();
    const prevScore = prev.myRating?.score ?? 0;
    if (prevScore === 0) return;

    this.myRating.set(undefined);
    this.summary.set(this.recomputeDelete(prev.summary, prevScore));

    this.busy.set(true);
    this.ratingService.clear(this.recipeId).subscribe({
      next: () => this.onMutated(),
      error: (err) => this.handleError(err, prev, 'Could not clear your rating.'),
    });
  }

  /** Persist a note from the editor, then tuck the editor away again. */
  onNoteSaved(comment: string): void {
    this.saveComment(comment);
    this.showComment.set(false);
  }

  /** Persist a note. Requires an existing score; does not touch the aggregate. */
  saveComment(comment: string): void {
    const current = this.myRating();
    if (!current || current.score < 1) return;

    const prev = this.snapshot();
    this.myRating.set({score: current.score, comment});

    this.busy.set(true);
    this.ratingService.rate(this.recipeId, current.score, comment).subscribe({
      next: () => {
        this.onMutated();
        this.toast.success('Note saved.');
      },
      error: (err) => this.handleError(err, prev, 'Could not save your note.'),
    });
  }

  // --- optimistic recompute (see the CRQ math) ---

  private recompute(s: RatingSummary, prevScore: number, score: number): RatingSummary {
    const {average: a, count: c} = s;
    if (prevScore === 0) {
      const newCount = c + 1;
      return {average: this.round1((a * c + score) / newCount), count: newCount};
    }
    // replacing a prior rating: count unchanged
    return {average: this.round1((a * c - prevScore + score) / c), count: c};
  }

  private recomputeDelete(s: RatingSummary, prevScore: number): RatingSummary {
    const {average: a, count: c} = s;
    const newCount = c - 1;
    return {
      average: newCount > 0 ? this.round1((a * c - prevScore) / newCount) : 0,
      count: Math.max(0, newCount),
    };
  }

  private round1(n: number): number {
    return Math.round(n * 10) / 10;
  }

  // --- snapshot / rollback ---

  private snapshot(): {summary: RatingSummary; myRating?: MyRating} {
    const m = this.myRating();
    return {
      summary: {...this.summary()},
      myRating: m ? {...m} : undefined,
    };
  }

  private handleError(err: unknown, prev: {summary: RatingSummary; myRating?: MyRating}, message: string): void {
    this.busy.set(false);
    // roll back optimistic state
    this.summary.set(prev.summary);
    this.myRating.set(prev.myRating);

    const status = (err as {status?: number})?.status;
    if (status === 403) {
      this.toast.error('You no longer have access to this recipe.');
      this.accessRevoked.emit();
      return;
    }

    this.toast.error(message);
    this.log.error('Recipe rating request failed', err, {recipeId: this.recipeId});
  }
}
